/**
 * @architecture-header 1
 * @layer shared
 * @domain workspace-shell
 * @responsibility Render the pointer-drag column resizer with double-click reset.
 * @state-owner navigation store owns persisted panel widths
 * @allowed-imports React; workspace store
 * @forbidden business rules; network
 * @public-api workspace shell and modules
 * @side-effects pointer capture on the window while dragging
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useRef,
  useState,
} from 'react';
import { type PanelKey, useWorkspaceUiStore } from '../model/workspace-store';

const PANEL_LABEL: Readonly<Record<PanelKey, string>> = {
  list: '調整清單欄寬度',
  ref: '調整參考原卷寬度',
  ivl: '調整學生清單寬度',
  ivr: '調整脈絡欄寬度',
  thr: '調整助教面板寬度',
};

export function PanelResizer({
  panel,
  min,
  max,
  side = 'r',
}: {
  panel: PanelKey;
  min: number;
  max: number;
  side?: 'l' | 'r';
}) {
  const width = useWorkspaceUiStore((state) => state.panelWidths[panel]);
  const setPanelWidth = useWorkspaceUiStore((state) => state.setPanelWidth);
  const resetPanelWidth = useWorkspaceUiStore((state) => state.resetPanelWidth);
  const [dragging, setDragging] = useState(false);
  const originRef = useRef({ x: 0, width: 0 });

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const direction = side === 'l' ? -1 : 1;
    originRef.current = { x: event.clientX, width };
    setDragging(true);
    document.body.style.cursor = 'col-resize';

    const onMove = (moveEvent: PointerEvent) => {
      const next = originRef.current.width + (moveEvent.clientX - originRef.current.x) * direction;
      setPanelWidth(panel, Math.max(min, Math.min(max, next)));
    };
    const onUp = () => {
      setDragging(false);
      document.body.style.cursor = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 24 : 8;
    const direction = side === 'l' ? -1 : 1;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setPanelWidth(panel, Math.max(min, Math.min(max, width - step * direction)));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setPanelWidth(panel, Math.max(min, Math.min(max, width + step * direction)));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      resetPanelWidth(panel);
    }
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: this is the WAI-ARIA window splitter pattern, which has no HTML equivalent
    <div
      aria-label={PANEL_LABEL[panel]}
      aria-orientation="vertical"
      aria-valuemax={max}
      aria-valuemin={min}
      aria-valuenow={width}
      className={`rzh${side === 'l' ? ' l' : ''}${dragging ? ' on' : ''}`}
      onDoubleClick={() => resetPanelWidth(panel)}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      role="separator"
      tabIndex={0}
    />
  );
}
